import { IconButton, Tooltip } from '@mui/material';
import CloudGpxUploader from '../../frame/util/CloudGpxUploader';
import React, { useContext, useEffect, useState } from 'react';
import AppContext, { FAVORITES_URL_PARAM_FOLDER } from '../../context/AppContext';
import { ReactComponent as ImportIcon } from '../../assets/icons/ic_action_folder_import_outlined.svg';
import { ReactComponent as AddFolderIcon } from '../../assets/icons/ic_action_folder_add_outlined.svg';
import { ReactComponent as AddTrackIcon } from '../../assets/icons/ic_action_track_add.svg';
import styles from '../trackfavmenu.module.css';
import TracksManager, { DEFAULT_GROUP_NAME } from '../../manager/track/TracksManager';
import { FREE_ACCOUNT } from '../../manager/LoginManager';
import AddFolderDialog from '../../dialogs/tracks/AddFolderDialog';
import { byTime } from './SortActions';
import { DEFAULT_FAV_GROUP_NAME } from '../../manager/FavoritesManager';
import FavoriteGroupUploader from '../../frame/util/FavoriteGroupUploader';
import IconButtonWithPermissions from '../../frame/components/IconButtonWithPermissions';
import { useTranslation } from 'react-i18next';
import { closeHeader } from './HeaderHelper';
import { confirm } from '../../dialogs/GlobalConfirmationDialog';
import SortFilesButton, { getSelectedSort } from '../components/buttons/SortFilesButton';
import LoginContext from '../../context/LoginContext';
import { SHARE_TYPE, SMART_TYPE } from '../share/shareConstants';
import ActionIconBtn from '../../frame/components/btns/ActionIconBtn';
import { useUpdateQueryParam } from '../../util/hooks/menu/useUpdateQueryParam';
import { FAVORITES_URL, MAIN_URL_WITH_SLASH } from '../../manager/GlobalManager';
import { useZoomToFit } from '../../util/hooks/map/useZoomToFit';
import { useFocusMode } from '../../util/hooks/map/useFocusMode';
import HeaderWithUnderline from '../../frame/components/header/HeaderWithUnderline';

export default function GroupHeader({
    type,
    smartf = null,
    trackGroup = null,
    favoriteGroup = null,
    setSortGroups = null,
    setSortFiles = null,
    markers = null,
    onClose = null,
}) {
    const ctx = useContext(AppContext);
    const ltx = useContext(LoginContext);

    const { updateQueryParam, searchParams } = useUpdateQueryParam();
    const { restoreMapView } = useZoomToFit();
    const { clearSelectionFocus } = useFocusMode();

    const TRACKS_TYPE = 'tracks';
    const FAVORITES_TYPE = 'favorites';

    const { t } = useTranslation();

    const [openAddFolderDialog, setOpenAddFolderDialog] = useState(false);

    const hiddenBtn = smartf?.type === SHARE_TYPE || trackGroup?.type === SMART_TYPE;

    useEffect(() => {
        if (favoriteGroup && setSortFiles && !getSelectedSort({ favoriteGroup, ctx })) {
            const files = ctx.favorites.mapObjs[favoriteGroup.id]?.wpts;
            if (files) {
                setSortFiles(byTime(files, true));
            }
        }
    }, [favoriteGroup, ctx.favorites]);

    function prevTrackMenu() {
        if (type === TRACKS_TYPE) {
            ctx.openGroups.pop();
            ctx.setOpenGroups([...ctx.openGroups]);
        } else if (type === FAVORITES_TYPE) {
            if (onClose) {
                onClose();
            } else {
                updateQueryParam({
                    key: FAVORITES_URL_PARAM_FOLDER,
                    value: null,
                    expectedLocation: MAIN_URL_WITH_SLASH + FAVORITES_URL,
                    replace: false,
                });
            }
        }
        restoreMapView();
        clearSelectionFocus();
    }

    function getGroupsCount() {
        if (type === TRACKS_TYPE) {
            return ctx.openGroups?.length || 0;
        } else if (type === FAVORITES_TYPE) {
            return searchParams.get(FAVORITES_URL_PARAM_FOLDER) || onClose ? 1 : 0;
        }
        return 0;
    }

    function getTitle() {
        if (smartf?.type === SHARE_TYPE) {
            return t('web:shared_with_me');
        }
        if (type === TRACKS_TYPE) {
            return trackGroup?.name === DEFAULT_GROUP_NAME || !trackGroup
                ? t('shared_string_tracks')
                : trackGroup?.name;
        }
        if (type === FAVORITES_TYPE) {
            return favoriteGroup === DEFAULT_FAV_GROUP_NAME || !favoriteGroup
                ? t('shared_string_favorites')
                : favoriteGroup?.name;
        }
    }

    function getTitleId() {
        if (smartf?.type === SHARE_TYPE) return undefined;
        if (type === TRACKS_TYPE) return 'se-cloud-name-track';
        if (type === FAVORITES_TYPE) return 'se-fav-group-name';
        return undefined;
    }

    function closeGroups() {
        if (type === TRACKS_TYPE) {
            ctx.setOpenGroups([]);
        } else if (type === FAVORITES_TYPE) {
            if (onClose) {
                onClose();
            } else {
                updateQueryParam({
                    key: FAVORITES_URL_PARAM_FOLDER,
                    value: null,
                    expectedLocation: MAIN_URL_WITH_SLASH + FAVORITES_URL,
                    replace: false,
                });
            }
        }
        closeHeader({ ctx });
        restoreMapView();
        clearSelectionFocus();
    }

    const showBackButton = getGroupsCount() > 0;

    return (
        <>
            <HeaderWithUnderline
                title={getTitle()}
                titleId={getTitleId()}
                onClose={showBackButton ? prevTrackMenu : closeGroups}
                showBackButton={showBackButton}
                appBarProps={{
                    id: showBackButton ? `se-back-folder-button-${type}` : `se-close-folder-button-${type}`,
                }}
                rightContent={
                    <>
                        <SortFilesButton
                            type={type}
                            trackGroup={trackGroup}
                            favoriteGroup={favoriteGroup}
                            setSortGroups={setSortGroups}
                            setSortFiles={setSortFiles}
                            markers={markers}
                            smartf={smartf}
                        />
                        {type === TRACKS_TYPE && !hiddenBtn && (
                            <Tooltip key={'add_folder'} title={t('add_new_folder')} arrow placement="bottom-end">
                                <span>
                                    <IconButtonWithPermissions
                                        id="se-add-folder"
                                        variant="contained"
                                        type="button"
                                        disabled={ltx.accountInfo?.account === FREE_ACCOUNT}
                                        className={styles.appBarIcon}
                                        onClick={() => setOpenAddFolderDialog(true)}
                                    >
                                        <AddFolderIcon />
                                    </IconButtonWithPermissions>
                                </span>
                            </Tooltip>
                        )}
                        {type === TRACKS_TYPE && !hiddenBtn && (
                            <Tooltip key={'import_track'} title={t('import_tracks')} arrow placement="bottom-end">
                                <span>
                                    <CloudGpxUploader folder={trackGroup ? trackGroup?.fullName : DEFAULT_GROUP_NAME}>
                                        <IconButtonWithPermissions
                                            id="se-import-cloud-track"
                                            component="span"
                                            variant="contained"
                                            type="button"
                                            disabled={ltx.accountInfo?.account === FREE_ACCOUNT}
                                            className={styles.appBarIcon}
                                        >
                                            <ImportIcon />
                                        </IconButtonWithPermissions>
                                    </CloudGpxUploader>
                                </span>
                            </Tooltip>
                        )}
                        {type === TRACKS_TYPE && !hiddenBtn && (
                            <Tooltip
                                key={'create_new_route'}
                                title={t('plan_route_create_new_route')}
                                arrow
                                placement="bottom-end"
                            >
                                <span>
                                    <ActionIconBtn
                                        id={'se-create-new-route'}
                                        onClick={() =>
                                            confirm({
                                                ctx,
                                                title: 'Plan Route: new track',
                                                text: 'Stop editing the current track?',
                                                skip: ctx.createTrack?.enable !== true,
                                                callback: () => TracksManager.createTrack(ctx),
                                            })
                                        }
                                        icon={<AddTrackIcon />}
                                    />
                                </span>
                            </Tooltip>
                        )}
                        {type === FAVORITES_TYPE && !hiddenBtn && (
                            <Tooltip
                                key={'import_fav_group'}
                                title={t('web:import_favorite_groups')}
                                arrow
                                placement="bottom-end"
                            >
                                <span>
                                    <FavoriteGroupUploader>
                                        <IconButton
                                            id="se-import-fav-group"
                                            component="span"
                                            variant="contained"
                                            type="button"
                                            className={styles.appBarIcon}
                                        >
                                            <ImportIcon />
                                        </IconButton>
                                    </FavoriteGroupUploader>
                                </span>
                            </Tooltip>
                        )}
                    </>
                }
            />
            {openAddFolderDialog && (
                <AddFolderDialog trackGroup={trackGroup} setOpenAddFolderDialog={setOpenAddFolderDialog} />
            )}
        </>
    );
}
